import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Play,
  Monitor,
  Calendar,
  Clock,
  Activity,
  Download,
  RefreshCw,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function AnalyticsPage() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [revenueTrends, setRevenueTrends] = useState(null);
  const [screens, setScreens] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [screenFilter, setScreenFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });
  const [quickRange, setQuickRange] = useState('30d');

  useEffect(() => {
    loadFiltersData();
  }, []);

  useEffect(() => {
    loadAnalytics();
    loadRevenueTrends();
  }, [dateRange, screenFilter, campaignFilter]);

  const loadFiltersData = async () => {
    try {
      const [screensRes, campaignsRes] = await Promise.all([
        api.get('/screens'),
        api.get('/campaigns'),
      ]);
      setScreens(screensRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      console.error('Failed to load filters data');
    }
  };

  const handleQuickRange = (range) => {
    setQuickRange(range);
    const now = new Date();
    let start;
    switch (range) {
      case '7d':
        start = subDays(now, 7);
        break;
      case '30d':
        start = subDays(now, 30);
        break;
      case '90d':
        start = subDays(now, 90);
        break;
      case 'mtd':
        start = startOfMonth(now);
        break;
      default:
        start = subDays(now, 30);
    }
    setDateRange({
      start: format(start, 'yyyy-MM-dd'),
      end: format(now, 'yyyy-MM-dd'),
    });
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const params = {
        start_date: dateRange.start,
        end_date: dateRange.end,
        ...(screenFilter !== 'all' && { screen_id: screenFilter }),
        ...(campaignFilter !== 'all' && { campaign_id: campaignFilter }),
      };
      const response = await api.get('/analytics/advanced', { params });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadRevenueTrends = async () => {
    try {
      const response = await api.get('/analytics/revenue-trends', {
        params: { months: 6 },
      });
      setRevenueTrends(response.data.trends);
    } catch (error) {
      console.error('Failed to load revenue trends');
    }
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const hourlyData = analytics?.hourly_distribution?.map((h) => ({
    hour: `${h.hour}:00`,
    plays: h.plays,
  })) || [];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Advanced Analytics
          </h1>
          <p className="text-zinc-400 mt-1">
            Detailed insights and performance metrics
          </p>
        </div>
        <Button
          variant="outline"
          onClick={loadAnalytics}
          className="border-zinc-700"
          data-testid="refresh-analytics-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Date Range Selector */}
      <Card className="glass-card border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Quick Date Range Buttons */}
            <div className="flex flex-wrap gap-2">
              {['7d', '30d', '90d', 'mtd'].map((range) => (
                <Button
                  key={range}
                  variant={quickRange === range ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleQuickRange(range)}
                  className={quickRange === range ? 'bg-blue-600' : 'border-zinc-700'}
                  data-testid={`date-range-${range}`}
                >
                  {range === 'mtd' ? 'This Month' : range.replace('d', ' Days')}
                </Button>
              ))}
            </div>
            
            {/* Filters Row */}
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Screen</Label>
                <Select value={screenFilter} onValueChange={setScreenFilter}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white w-48">
                    <SelectValue placeholder="All Screens" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="all">All Screens</SelectItem>
                    {screens.map((screen) => (
                      <SelectItem key={screen.id} value={screen.id}>{screen.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Campaign</Label>
                <Select value={campaignFilter} onValueChange={setCampaignFilter}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white w-48">
                    <SelectValue placeholder="All Campaigns" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="all">All Campaigns</SelectItem>
                    {campaigns.map((campaign) => (
                      <SelectItem key={campaign.id} value={campaign.id}>{campaign.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => {
                    setDateRange({ ...dateRange, start: e.target.value });
                    setQuickRange('custom');
                  }}
                  className="bg-zinc-900 border-zinc-800 text-white w-40"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">End Date</Label>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => {
                    setDateRange({ ...dateRange, end: e.target.value });
                    setQuickRange('custom');
                  }}
                  className="bg-zinc-900 border-zinc-800 text-white w-40"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card border-zinc-800 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Play className="w-6 h-6 text-blue-500" />
              </div>
              <TrendingUp className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {(analytics?.summary?.total_plays || 0).toLocaleString()}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Total Plays</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                ${(analytics?.summary?.total_revenue || 0).toLocaleString()}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Revenue</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Monitor className="w-6 h-6 text-purple-500" />
              </div>
              <Badge variant="outline" className="status-online">
                Active
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {analytics?.summary?.active_screens || 0}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Active Screens</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Activity className="w-6 h-6 text-amber-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {analytics?.summary?.active_campaigns || 0}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Active Campaigns</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Plays Chart */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Daily Plays
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics?.daily_plays || []}>
                  <defs>
                    <linearGradient id="playsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis
                    dataKey="date"
                    stroke="#71717a"
                    fontSize={12}
                    tickFormatter={(value) => format(new Date(value), 'MMM d')}
                  />
                  <YAxis stroke="#71717a" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181b',
                      border: '1px solid #27272a',
                      borderRadius: '8px',
                    }}
                    labelFormatter={(value) => format(new Date(value), 'MMM d, yyyy')}
                  />
                  <Area
                    type="monotone"
                    dataKey="plays"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#playsGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Trends */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Revenue Trends (6 Months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueTrends || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="month_name" stroke="#71717a" fontSize={12} />
                  <YAxis stroke="#71717a" fontSize={12} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181b',
                      border: '1px solid #27272a',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [`$${value}`, 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hourly Distribution */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Hourly Play Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="hour" stroke="#71717a" fontSize={10} />
                <YAxis stroke="#71717a" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#18181b',
                    border: '1px solid #27272a',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="plays" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Performance Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Screen Performance */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Top Performing Screens
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Screen</TableHead>
                  <TableHead className="text-zinc-500 text-right">Plays</TableHead>
                  <TableHead className="text-zinc-500 text-right">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analytics?.screen_performance?.slice(0, 5).map((screen, index) => (
                  <TableRow key={index} className="border-zinc-800">
                    <TableCell className="text-white font-medium">
                      {screen.screen_name}
                    </TableCell>
                    <TableCell className="text-zinc-400 text-right">
                      {screen.plays.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-emerald-400 text-right font-medium">
                      ${screen.revenue.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
                {(!analytics?.screen_performance || analytics.screen_performance.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-zinc-500 py-8">
                      No data for selected period
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Content Performance */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Top Content
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Content</TableHead>
                  <TableHead className="text-zinc-500">Type</TableHead>
                  <TableHead className="text-zinc-500 text-right">Plays</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analytics?.content_performance?.slice(0, 5).map((content, index) => (
                  <TableRow key={index} className="border-zinc-800">
                    <TableCell className="text-white font-medium">
                      {content.content_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                        {content.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-400 text-right">
                      {content.plays.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
                {(!analytics?.content_performance || analytics.content_performance.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-zinc-500 py-8">
                      No data for selected period
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Campaign Analytics */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Campaign Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500">Campaign</TableHead>
                <TableHead className="text-zinc-500 text-right">Plays</TableHead>
                <TableHead className="text-zinc-500 text-right">Impressions</TableHead>
                <TableHead className="text-zinc-500 text-right">Budget</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analytics?.campaign_analytics?.map((campaign, index) => (
                <TableRow key={index} className="border-zinc-800">
                  <TableCell className="text-white font-medium">
                    {campaign.campaign_name}
                  </TableCell>
                  <TableCell className="text-zinc-400 text-right">
                    {campaign.plays.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-zinc-400 text-right">
                    {campaign.impressions.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-amber-400 text-right font-medium">
                    ${campaign.budget.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
              {(!analytics?.campaign_analytics || analytics.campaign_analytics.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-zinc-500 py-8">
                    No active campaigns
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
